import React, { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'

// Project profile — fair selection. Prompted once the project has taken shape
// (Step 3+), so trial exploration (Steps 1–2) stays friction-free. Writes the
// fair to the PROJECT (project_fair_entry), not the student.

interface FairOption { id: string; name: string; county: string | null; state: string | null }
type Qualifying = 'yes' | 'no' | 'not_sure' | ''

// currentFairId / currentQualifying prefill the dialog when it is opened to
// CHANGE an existing choice rather than make a first one — so a student
// correcting a mis-pick sees what they picked, not an empty box.
export default function FairProfileDialog({
  projectId, open, onClose, onSaved, currentFairId, currentQualifying,
}: {
  projectId: string; open: boolean; onClose: () => void; onSaved: () => void
  currentFairId?: string | null; currentQualifying?: string | null
}) {
  const [fairs,      setFairs]      = useState<FairOption[]>([])
  const [fair,       setFair]       = useState<FairOption | null>(null)
  const [qualifying, setQualifying] = useState<Qualifying>('')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    supabase.from('science_fair').select('id, name, county, state').order('name')
      .then(({ data }) => {
        const opts = (data ?? []) as FairOption[]
        setFairs(opts)
        /* Seed from the existing entry each time it opens, so reopening after a
           cancel shows the saved choice rather than a half-made edit. */
        setFair(currentFairId ? (opts.find(o => o.id === currentFairId) ?? null) : null)
      })
    setQualifying((currentQualifying as Qualifying) ?? '')
  }, [open, currentFairId, currentQualifying])

  async function saveFair() {
    if (!fair) return
    setSaving(true); setError(null)
    const { error: e } = await supabase.from('project_fair_entry').upsert({
      project_id: projectId, science_fair_id: fair.id, qualifying_fair: qualifying || null,
    }, { onConflict: 'project_id' })
    setSaving(false)
    if (e) { setError('Could not save — please try again.'); return }
    onSaved()
  }

  async function notCompeting() {
    setSaving(true); setError(null)
    const { error: e } = await supabase.from('project').update({ fair_opt_out: true }).eq('project_id', projectId)
    setSaving(false)
    if (e) { setError('Could not save — please try again.'); return }
    onSaved()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Which science fair are you aiming for?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          Your project is taking shape — let us know which fair you plan to enter, and we'll keep the
          important dates in front of you (and your parents). You can change this anytime.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Autocomplete
          options={fairs}
          getOptionLabel={o => o.county ? `${o.name} (${o.county} County)` : o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={fair} onChange={(_, v) => setFair(v)}
          renderInput={p => <TextField {...p} label="Science fair" size="small" />}
          sx={{ mb: 2.5 }}
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Do you have a qualifying (school or district) science fair?
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          County fairs like OCSEF usually require you to be selected at your school or district fair first.
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={qualifying} onChange={(_, v) => { if (v) setQualifying(v) }}>
          <ToggleButton value="yes">Yes</ToggleButton>
          <ToggleButton value="no">No</ToggleButton>
          <ToggleButton value="not_sure">Not sure</ToggleButton>
        </ToggleButtonGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          <Button size="small" color="inherit" onClick={notCompeting} disabled={saving}>I'm not competing</Button>
          <Button size="small" color="inherit" onClick={onClose} disabled={saving}>Decide later</Button>
        </Box>
        <Button variant="contained" onClick={saveFair} disabled={saving || !fair}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}
